import { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap";
import { postLogin } from "../../helpers/backend_helper";
import CheckInMap from "./checkInMap";
import { exportToExcel } from "../../helpers/general";
import ModernCheckInModal from "./checkinModel";
import { Link } from "react-router-dom";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import { timeStamptoDate } from "../../helpers/formatDate";
import Datatable from "../../components/Common/Datatable/Datatable";

export default function UserCheckInTable() {
  // State management
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkInData, setCheckInData] = useState(null);
  
  // Filter states
  const [appliedFilters, setAppliedFilters] = useState({
    date: (() => {
      const today = new Date();
      return today.toISOString().slice(0, 10);
    })(),
    search: "",
    statusFilter: "all"
  });
  
  const [tempFilters, setTempFilters] = useState({
    date: appliedFilters.date,
    search: "",
    statusFilter: "all"
  });

  // Data states
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState("1");

  // Filter handlers
  const handleStatusFilterChange = (e) => {
    setTempFilters(prev => ({
      ...prev,
      statusFilter: e.target.value
    }));
  };

  const handleSearchChange = (e) => {
    setTempFilters(prev => ({
      ...prev,
      search: e.target.value
    }));
  };

  const handleDateChange = (timestamp) => {
    const newDate = timeStamptoDate(timestamp);
    setTempFilters(prev => ({
      ...prev,
      date: newDate
    }));
  };

  // Apply filters function
  const applyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    if (tempFilters.date !== appliedFilters.date) {
      fetchUsers(tempFilters.date);
    }
  };

  // Reset filters function
  const resetFilters = () => {
    const today = new Date().toISOString().slice(0, 10);
    const resetFilters = {
      date: today,
      search: "",
      statusFilter: "all"
    };
    
    setTempFilters(resetFilters);
    setAppliedFilters(resetFilters);
    fetchUsers(today);
  };

  // Fetch users by date
  const fetchUsers = async (filterDate = appliedFilters.date) => {
    try {
      const payload = {
        act: "user",
        subact: "getCheckIns",
        info: {
          date: filterDate.replace(/-/g, ""),
        },
      };
      const response = await postLogin(payload);
      setUsers(response?.data?.user || []);
      setVehicles(response?.data?.vehicle || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setVehicles([]);
    }
  };

  // Effect to fetch initial data
  useEffect(() => {
    fetchUsers(appliedFilters.date);
  }, []);

  // Apply search and status filters
  let filteredUsers = users?.filter(
  (user) =>
  !appliedFilters.search ||
  user.firstName?.toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
  user.lastName?.toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
  `${user?.firstName?.trim() ?? ''} ${user?.lastName?.trim() ?? ''}`.trim().toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
  user.email?.toLowerCase().includes(appliedFilters.search.toLowerCase()) ||
  user.contactNumber?.toLowerCase().includes(appliedFilters.search.toLowerCase())
);

  const allFilteredUsers = filteredUsers;

  // Apply status filter
  filteredUsers = filteredUsers?.filter((user) => {
    if (appliedFilters.statusFilter === "all") return true;
    if (appliedFilters.statusFilter === "checkin") return user.checkIn;
    if (appliedFilters.statusFilter === "no_checkin") return !user.checkIn;
    return true;
  });

  // Calculate counts based on search results (before status filter)
  const totalCount = allFilteredUsers?.length || 0;
  const checkedInCount = allFilteredUsers?.filter((user) => user.checkIn)?.length || 0;
  const notCheckedInCount = allFilteredUsers?.filter((user) => !user.checkIn)?.length || 0;

  // Table columns configuration
  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      sortable: true,
      cell: (row) => (
        <span style={{ textTransform: "capitalize" }}>
          {`${row.firstName} ${row.lastName}`}
        </span>
      ),
    },
    { 
      name: "Email", 
      selector: (row) => row.email, 
      sortable: true 
    },
    { 
      name: "Contact", 
      selector: (row) => row.contactNumber, 
      sortable: true 
    },
    {
      name: "Location",
      cell: (row) =>
        row.location
          ? `${row.location.latitude.toFixed(4)}, ${row.location.longitude.toFixed(4)}`
          : "-",
    },
    {
      name: "Check-In",
      selector: (row) => (row.checkIn ? "✅" : "❌"),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          color="primary"
          size="sm"
          onClick={() => handleActionClick(row)}
        >
          View
        </Button>
      ),
    },
  ];

  // Modal handlers
  const handleActionClick = async (user) => {
    setSelectedUser(user);
    setModalOpen(true);
    setCheckInData(null);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setCheckInData(null);
  };

  // Tab handlers
  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <>
      {/* Filter Controls Card */}
      <Card className="shadow-sm mb-4">
        <CardBody>
          <Row className="align-items-end g-3">
            {/* Date Filter */}
            <Col md={2}>
              <CommonDatePicker
                label="Date"
                value={tempFilters.date}
                maxDate={new Date().toISOString().slice(0, 10)}
                onChange={handleDateChange}
                name="Date"
                preserveTime={true}
              />
            </Col>

            {/* Search Filter */}
            <Col md={3}>
              <Label for="search" className="form-label">
                Search
              </Label>
              <InputGroup>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search by name, email, contact..."
                  value={tempFilters.search}
                  onChange={handleSearchChange}
                />
                <InputGroupText>
                  <i className="ri-search-line" />
                </InputGroupText>
              </InputGroup>
            </Col>

            {/* Status Filter */}
            <Col md={2}>
              <Label for="statusFilter" className="form-label">
                Status
              </Label>
              <Input
                type="select"
                id="statusFilter"
                value={tempFilters.statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="all">All</option>
                <option value="checkin">Check-in</option>
                <option value="no_checkin">No Check-in</option>
              </Input>
            </Col>

            {/* Filter Action Buttons */}
            <Col md={2} className="d-flex gap-2">
              <Button 
                color="primary" 
                onClick={applyFilters}
              >
                 Apply
              </Button>
              <Button 
                color="secondary" 
                 
                onClick={resetFilters}
              >
                Reset
              </Button>
            </Col>

            <Col md={3} className="text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Link to="/checkin-report">
                  <Button color="info" className="d-flex align-items-center">
                    <i className="ri-file-list-line me-1" />
                    Check-In Report
                  </Button>
                </Link>
                <Button
                  color="success"
                  onClick={() => exportToExcel(filteredUsers)}
                  className="d-flex align-items-center"
                >
                  <i className="ri-file-excel-line me-1" />
                  Export Excel
                </Button>
              </div>
            </Col>
            </Row>
        </CardBody>
      </Card>

      {/* Main Content Card */}
      <Card className="shadow-sm">
        <CardBody>
          {/* Header with Tabs and Statistics */}
          <Row className="mb-4">
            <Col lg={4}>
              <Nav tabs className="nav-tabs-custom">
                <NavItem>
                  <NavLink
                    className={`${activeTab === "1" ? "active" : ""} d-flex align-items-center`}
                    onClick={() => toggleTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="ri-team-line me-2" />
                    Guard List
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`${activeTab === "2" ? "active" : ""} d-flex align-items-center`}
                    onClick={() => toggleTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="ri-map-pin-line me-2" />
                    Map View
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>

            {/* Statistics Badges */}
            <Col lg={8} className="d-flex justify-content-end align-items-center">
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium text-muted">Total:</span>
                  {/* <Badge color="primary" className="fs-6 px-3 py-2 rounded-pill"> */}
                    {totalCount}
                  {/* </Badge> */}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium text-muted">Check-in:</span>
                  <Badge color="success" className="fs-6 px-3 py-2 rounded-pill">
                    {checkedInCount}
                  </Badge>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span className="fw-medium text-muted">No Check-in:</span>
                  <Badge color="danger" className="fs-6 px-3 py-2 rounded-pill">
                    {notCheckedInCount}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          {/* Tab Content */}
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <div className="table-responsive">
                <Datatable 
                  columns={columns} 
                  data={filteredUsers} 
                  pagination={true}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            </TabPane>
            <TabPane tabId="2">
              {/* Empty TabPane for Map - actual map is rendered outside */}
            </TabPane>
          </TabContent>

          {/* Map Component - Always rendered but visibility controlled */}
          <div 
            className="map-container" 
            style={{ 
              minHeight: "500px",
              visibility: activeTab === "2" ? "visible" : "hidden",
              height: activeTab === "2" ? "auto" : "0",
              overflow: "hidden"
            }}
          >
            <CheckInMap 
              filteredUsers={filteredUsers} 
              vehicles={vehicles} 
            />
          </div>
        </CardBody>
      </Card>

      {/* User Details Modal */}
      <Modal 
        isOpen={modalOpen} 
        toggle={handleClose} 
        size="lg" 
        centered
        className="modern-modal"
      >
        <ModalHeader
          toggle={handleClose}
          className="bg-light border-bottom-0 pb-0"
        >
          <div className="d-flex flex-column">
            <div className="fw-bold fs-5 text-dark">
              {selectedUser?.firstName} {selectedUser?.lastName}
            </div>
            <div className="small text-muted mt-1">
              <i className="ri-mail-line me-1" />
              {selectedUser?.email}
              <span className="mx-2">|</span>
              <i className="ri-phone-line me-1" />
              {selectedUser?.contactNumber}
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody className="pt-3">
          <ModernCheckInModal 
            key={checkInData?.user} 
            userId={selectedUser} 
          />
        </ModalBody>
        
        <ModalFooter className="border-top-0 pt-0">
          <Button 
            color="secondary" 
            onClick={handleClose}
            className="px-4"
          >
            <i className="ri-close-line me-1" />
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}